import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ArrowLeft, Users, Wrench, FileText, Clock, Package, ClipboardList, Shield, Plus, Trash2, Edit, UserPlus, UserMinus, Box } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { fmtDate } from '../lib/dateUtils';
import ProjectTankPanel from '../components/ProjectTankPanel';

const STATUS_COLORS = {
  pianificato: 'bg-slate-100 text-slate-700 border-slate-300',
  in_corso: 'bg-blue-50 text-blue-700 border-blue-300',
  completato: 'bg-green-50 text-green-700 border-green-300',
  sospeso: 'bg-amber-50 text-amber-700 border-amber-300',
};

const PDO_STATUS_COLORS = {
  aperto: 'bg-green-50 text-green-700 border-green-300',
  chiuso: 'bg-slate-100 text-slate-700 border-slate-300',
  sospeso: 'bg-amber-50 text-amber-700 border-amber-300',
};

export default function ProjectDetailPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { isRole } = useAuth();
  const canManage = isRole('admin', 'aspp', 'capo_cantiere', 'qhse');

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allEmployees, setAllEmployees] = useState([]);
  const [allEquipment, setAllEquipment] = useState([]);
  const [tab, setTab] = useState('overview');

  // PDO form
  const [showPdoCreate, setShowPdoCreate] = useState(false);
  const [showPdoEdit, setShowPdoEdit] = useState(null);
  const [pdoForm, setPdoForm] = useState({
    numero: '', tipo: 'generico', descrizione: '', data_apertura: '', data_chiusura: '',
    status: 'aperto', worker_ids: [], attivita: '', area_lavoro: '', rischi: '', misure_sicurezza: '', note: ''
  });

  // Assign dialogs
  const [showAssignEmp, setShowAssignEmp] = useState(false);
  const [showAssignEquip, setShowAssignEquip] = useState(false);
  const [assignSearch, setAssignSearch] = useState('');

  const load = useCallback(async () => {
    try {
      const [pRes, empRes, eqRes] = await Promise.all([
        api.get(`/projects/${projectId}`),
        canManage ? api.get('/employees').catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        canManage ? api.get('/equipment').catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      ]);
      setProject(pRes.data);
      setAllEmployees(empRes.data.filter(e => e.is_active));
      setAllEquipment(eqRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [projectId, canManage]);

  useEffect(() => { load(); }, [load]);

  const assignEmployee = async (empId) => {
    try {
      await api.post(`/projects/${projectId}/assign-employee`, { employee_id: empId });
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const unassignEmployee = async (empId) => {
    try {
      await api.post(`/projects/${projectId}/unassign-employee`, { employee_id: empId });
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const assignEquipment = async (eqId) => {
    try {
      await api.post(`/projects/${projectId}/assign-equipment`, { equipment_id: eqId });
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const unassignEquipment = async (eqId) => {
    try {
      await api.post(`/projects/${projectId}/unassign-equipment`, { equipment_id: eqId });
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  // PDO CRUD
  const openPdoCreate = () => {
    setPdoForm({
      numero: '', tipo: 'generico', descrizione: '', data_apertura: new Date().toISOString().split('T')[0],
      data_chiusura: '', status: 'aperto', worker_ids: [], attivita: '', area_lavoro: '', rischi: '', misure_sicurezza: '', note: ''
    });
    setShowPdoCreate(true);
  };

  const handlePdoCreate = async () => {
    try {
      await api.post(`/projects/${projectId}/pdo`, pdoForm);
      setShowPdoCreate(false);
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore creazione PDO'); }
  };

  const openPdoEdit = (pdo) => {
    setPdoForm({
      numero: pdo.numero || '', tipo: pdo.tipo || 'generico', descrizione: pdo.descrizione || '',
      data_apertura: pdo.data_apertura || '', data_chiusura: pdo.data_chiusura || '',
      status: pdo.status || 'aperto', worker_ids: pdo.worker_ids || [],
      attivita: pdo.attivita || '', area_lavoro: pdo.area_lavoro || '',
      rischi: pdo.rischi || '', misure_sicurezza: pdo.misure_sicurezza || '', note: pdo.note || ''
    });
    setShowPdoEdit(pdo);
  };

  const handlePdoEdit = async () => {
    try {
      await api.put(`/projects/${projectId}/pdo/${showPdoEdit.id}`, pdoForm);
      setShowPdoEdit(null);
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore aggiornamento PDO'); }
  };

  const handlePdoDelete = async (pdoId) => {
    if (!window.confirm('Eliminare questo PDO?')) return;
    try {
      await api.delete(`/projects/${projectId}/pdo/${pdoId}`);
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore eliminazione PDO'); }
  };

  const togglePdoWorker = (empId) => {
    setPdoForm(prev => ({
      ...prev,
      worker_ids: prev.worker_ids.includes(empId)
        ? prev.worker_ids.filter(id => id !== empId)
        : [...prev.worker_ids, empId]
    }));
  };

  if (loading) return <div className="p-6 text-center text-slate-500">Caricamento progetto...</div>;
  if (!project) return <div className="p-6 text-center text-red-500">Progetto non trovato</div>;

  const pdos = project.pdos || [];
  const materials = project.materials || [];
  const attendance = project.attendance || [];
  const salEntries = project.sal_entries || [];
  const assignedEmpIds = project.assigned_employees || [];
  const assignedEqIds = project.assigned_equipment || [];

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="project-detail-page">
      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="ghost" size="sm" onClick={() => navigate('/projects')} data-testid="back-to-projects">
          <ArrowLeft size={16} className="mr-1" /> Progetti
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl md:text-2xl font-bold text-slate-800 truncate">{project.name}</h1>
          <p className="text-sm text-slate-500">{project.site_name} {project.site_client ? `- ${project.site_client}` : ''}</p>
        </div>
        <Badge variant="outline" className={STATUS_COLORS[project.status] || ''} data-testid="project-status">
          {project.status?.replace('_', ' ')}
        </Badge>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card>
          <CardContent className="p-3 text-center">
            <p className="text-xs text-slate-500">Avanzamento</p>
            <p className="text-lg font-bold text-slate-800">{project.progress_percentage || 0}%</p>
            <Progress value={project.progress_percentage || 0} className="h-1.5 mt-1" />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 text-center">
            <p className="text-xs text-slate-500">Ore Lavorate</p>
            <p className="text-lg font-bold text-blue-700" data-testid="total-hours">{project.total_hours_worked || 0}h</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 text-center">
            <p className="text-xs text-slate-500">Personale</p>
            <p className="text-lg font-bold text-green-700" data-testid="personnel-count">{(project.employees_detail || []).length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 text-center">
            <p className="text-xs text-slate-500">PDO Aperti</p>
            <p className="text-lg font-bold text-amber-700" data-testid="pdo-open-count">{pdos.filter(p => p.status === 'aperto').length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 text-center">
            <p className="text-xs text-slate-500">Voci SAL</p>
            <p className="text-lg font-bold text-purple-700" data-testid="sal-count">{salEntries.length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="flex flex-wrap h-auto gap-1 bg-slate-100 p-1 rounded-lg">
          <TabsTrigger value="overview" className="text-xs md:text-sm" data-testid="tab-overview">Panoramica</TabsTrigger>
          <TabsTrigger value="personnel" className="text-xs md:text-sm" data-testid="tab-personnel"><Users size={14} className="mr-1 hidden md:inline" />Personale</TabsTrigger>
          <TabsTrigger value="equipment" className="text-xs md:text-sm" data-testid="tab-equipment"><Wrench size={14} className="mr-1 hidden md:inline" />Attrezzature</TabsTrigger>
          <TabsTrigger value="pdo" className="text-xs md:text-sm" data-testid="tab-pdo"><Shield size={14} className="mr-1 hidden md:inline" />PDO</TabsTrigger>
          <TabsTrigger value="materials" className="text-xs md:text-sm" data-testid="tab-materials"><Package size={14} className="mr-1 hidden md:inline" />Materiali</TabsTrigger>
          <TabsTrigger value="attendance" className="text-xs md:text-sm" data-testid="tab-attendance"><Clock size={14} className="mr-1 hidden md:inline" />Presenze</TabsTrigger>
          <TabsTrigger value="sal" className="text-xs md:text-sm" data-testid="tab-sal"><ClipboardList size={14} className="mr-1 hidden md:inline" />SAL</TabsTrigger>
          <TabsTrigger value="tank" className="text-xs md:text-sm" data-testid="tab-tank"><Box size={14} className="mr-1 hidden md:inline" />Serbatoio 3D</TabsTrigger>
        </TabsList>

        {/* ─── OVERVIEW TAB ────────────────────── */}
        <TabsContent value="overview">
          <Card>
            <CardContent className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div><Label className="text-xs text-slate-500">Work Order</Label><p className="text-sm font-medium" data-testid="work-order">{project.work_order || '-'}</p></div>
                <div><Label className="text-xs text-slate-500">Purchase Order</Label><p className="text-sm font-medium" data-testid="purchase-order">{project.purchase_order || '-'}</p></div>
                <div><Label className="text-xs text-slate-500">Numeri PDL</Label>
                  <div className="flex flex-wrap gap-1 mt-0.5" data-testid="numeri-pdl">
                    {project.numero_pdl ? project.numero_pdl.split(',').map((pdl, i) => (
                      <span key={i} className="inline-block px-2 py-0.5 bg-slate-100 border border-slate-200 rounded text-sm font-medium">{pdl.trim()}</span>
                    )) : <span className="text-sm text-slate-400">-</span>}
                  </div>
                </div>
                <div><Label className="text-xs text-slate-500">Referente Tecnico</Label><p className="text-sm font-medium" data-testid="referente-tecnico">{project.referente_tecnico || '-'}</p></div>
                <div><Label className="text-xs text-slate-500">Budget</Label><p className="text-sm font-medium">{project.budget ? `${Number(project.budget).toLocaleString('it-IT')} EUR` : '-'}</p></div>
              </div>
              <div className="space-y-3">
                <div><Label className="text-xs text-slate-500">Data Inizio</Label><p className="text-sm font-medium">{fmtDate(project.start_date)}</p></div>
                <div><Label className="text-xs text-slate-500">Data Fine</Label><p className="text-sm font-medium">{fmtDate(project.end_date)}</p></div>
                <div><Label className="text-xs text-slate-500">Cantiere</Label><p className="text-sm font-medium">{project.site_name}</p></div>
                <div><Label className="text-xs text-slate-500">Localita</Label><p className="text-sm font-medium">{project.site_location || '-'}</p></div>
              </div>
              {project.description && (
                <div className="col-span-1 md:col-span-2">
                  <Label className="text-xs text-slate-500">Descrizione</Label>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{project.description}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── PERSONNEL TAB ──────────────────── */}
        <TabsContent value="personnel">
          <Card>
            <CardHeader className="p-4 pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-base">Personale Assegnato ({(project.employees_detail || []).length})</CardTitle>
              {canManage && (
                <Button size="sm" onClick={() => { setAssignSearch(''); setShowAssignEmp(true); }} data-testid="assign-emp-btn">
                  <UserPlus size={14} className="mr-1" /> Assegna
                </Button>
              )}
            </CardHeader>
            <CardContent className="p-4 pt-0">
              {(project.employees_detail || []).length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">Nessun personale assegnato</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      <TableHead>Ruolo</TableHead>
                      <TableHead>Qualifica</TableHead>
                      {canManage && <TableHead className="w-16">Azioni</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(project.employees_detail || []).map(emp => (
                      <TableRow key={emp.id}>
                        <TableCell className="text-sm font-medium">{emp.first_name} {emp.last_name}</TableCell>
                        <TableCell className="text-sm text-slate-600">{emp.role || '-'}</TableCell>
                        <TableCell className="text-sm text-slate-600">{emp.qualification || '-'}</TableCell>
                        {canManage && (
                          <TableCell>
                            <Button variant="ghost" size="sm" onClick={() => unassignEmployee(emp.id)} className="p-1 h-auto text-red-600" data-testid={`unassign-emp-${emp.id}`}>
                              <UserMinus size={14} />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── EQUIPMENT TAB ──────────────────── */}
        <TabsContent value="equipment">
          <Card>
            <CardHeader className="p-4 pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-base">Attrezzature Assegnate ({(project.equipment_detail || []).length})</CardTitle>
              {canManage && (
                <Button size="sm" onClick={() => { setAssignSearch(''); setShowAssignEquip(true); }} data-testid="assign-equip-btn">
                  <Plus size={14} className="mr-1" /> Assegna
                </Button>
              )}
            </CardHeader>
            <CardContent className="p-4 pt-0">
              {(project.equipment_detail || []).length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">Nessuna attrezzatura assegnata</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Matricola</TableHead>
                      {canManage && <TableHead className="w-16">Azioni</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(project.equipment_detail || []).map(eq => (
                      <TableRow key={eq.id}>
                        <TableCell className="text-sm font-medium">{eq.name}</TableCell>
                        <TableCell className="text-sm text-slate-600">{eq.type || '-'}</TableCell>
                        <TableCell className="text-sm text-slate-600">{eq.serial_number || '-'}</TableCell>
                        {canManage && (
                          <TableCell>
                            <Button variant="ghost" size="sm" onClick={() => unassignEquipment(eq.id)} className="p-1 h-auto text-red-600" data-testid={`unassign-eq-${eq.id}`}>
                              <Trash2 size={14} />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── PDO TAB ────────────────────────── */}
        <TabsContent value="pdo">
          <Card>
            <CardHeader className="p-4 pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-base">Permessi di Lavoro (PDO) ({pdos.length})</CardTitle>
              {canManage && <Button size="sm" onClick={openPdoCreate} data-testid="create-pdo-btn"><Plus size={14} className="mr-1" /> Nuovo PDO</Button>}
            </CardHeader>
            <CardContent className="p-4 pt-0">
              {pdos.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">Nessun PDO per questo progetto</p>
              ) : (
                <div className="space-y-3">
                  {pdos.map(pdo => (
                    <Card key={pdo.id} className="border">
                      <CardContent className="p-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-sm font-bold text-slate-800">PDO #{pdo.numero || '---'}</span>
                              <Badge variant="outline" className={`text-xs ${PDO_STATUS_COLORS[pdo.status] || ''}`}>{pdo.status}</Badge>
                              <Badge variant="outline" className="text-xs bg-slate-50">{pdo.tipo}</Badge>
                            </div>
                            <p className="text-sm text-slate-600">{pdo.descrizione || pdo.attivita || 'Senza descrizione'}</p>
                            <div className="flex flex-wrap gap-3 mt-2 text-xs text-slate-500">
                              <span>Apertura: {fmtDate(pdo.data_apertura)}</span>
                              {pdo.data_chiusura && <span>Chiusura: {fmtDate(pdo.data_chiusura)}</span>}
                              {pdo.area_lavoro && <span>Area: {pdo.area_lavoro}</span>}
                            </div>
                            {(pdo.workers || []).length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {pdo.workers.map(w => (
                                  <Badge key={w.id} variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">{w.name}</Badge>
                                ))}
                              </div>
                            )}
                          </div>
                          {canManage && (
                            <div className="flex gap-1 shrink-0">
                              <Button variant="ghost" size="sm" onClick={() => openPdoEdit(pdo)} className="p-1 h-auto text-blue-600" data-testid={`edit-pdo-${pdo.id}`}><Edit size={14} /></Button>
                              <Button variant="ghost" size="sm" onClick={() => handlePdoDelete(pdo.id)} className="p-1 h-auto text-red-600" data-testid={`del-pdo-${pdo.id}`}><Trash2 size={14} /></Button>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── MATERIALS TAB ──────────────────── */}
        <TabsContent value="materials">
          <Card>
            <CardHeader className="p-4 pb-2">
              <CardTitle className="text-base">Materiali Utilizzati ({materials.length} movimenti)</CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0 overflow-x-auto">
              {materials.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">Nessun materiale collegato a questo progetto. Per collegare scarichi, seleziona questo progetto durante lo scarico magazzino.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data</TableHead>
                      <TableHead>Articolo</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead className="text-right">Quantita</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {materials.map(m => (
                      <TableRow key={m.id}>
                        <TableCell className="text-sm">{fmtDate(m.date)}</TableCell>
                        <TableCell className="text-sm font-medium">{m.item_name}</TableCell>
                        <TableCell><Badge variant="outline" className={m.movement_type === 'carico' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}>{m.movement_type}</Badge></TableCell>
                        <TableCell className="text-right text-sm font-medium">{m.quantity}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── ATTENDANCE TAB ─────────────────── */}
        <TabsContent value="attendance">
          <Card>
            <CardHeader className="p-4 pb-2">
              <CardTitle className="text-base">Presenze Progetto ({attendance.length} registrazioni, {project.total_hours_worked || 0}h totali)</CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0 overflow-x-auto">
              {attendance.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">Nessuna presenza collegata. Per collegare presenze, seleziona questo progetto nella timbratura.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data</TableHead>
                      <TableHead>Dipendente</TableHead>
                      <TableHead>Entrata</TableHead>
                      <TableHead>Uscita</TableHead>
                      <TableHead className="text-right">Ore</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {attendance.map(a => (
                      <TableRow key={a.id}>
                        <TableCell className="text-sm">{fmtDate(a.date)}</TableCell>
                        <TableCell className="text-sm font-medium">{a.employee_name}</TableCell>
                        <TableCell className="text-sm">{a.clock_in ? a.clock_in.split('T')[1]?.substring(0,5) : '-'}</TableCell>
                        <TableCell className="text-sm">{a.clock_out ? a.clock_out.split('T')[1]?.substring(0,5) : '-'}</TableCell>
                        <TableCell className="text-right text-sm font-medium">{a.hours_worked || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── SAL TAB ────────────────────────── */}
        <TabsContent value="sal">
          <Card>
            <CardHeader className="p-4 pb-2">
              <CardTitle className="text-base">SAL Progetto ({salEntries.length} voci)</CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0 overflow-x-auto">
              {salEntries.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">Nessuna voce SAL collegata. Per collegare SAL, seleziona questo progetto nella creazione SAL.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data</TableHead>
                      <TableHead>Descrizione</TableHead>
                      <TableHead className="text-right">Quantita</TableHead>
                      <TableHead className="text-right">Importo</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {salEntries.map(e => (
                      <TableRow key={e.id}>
                        <TableCell className="text-sm">{fmtDate(e.date)}</TableCell>
                        <TableCell className="text-sm">{e.description || '-'}</TableCell>
                        <TableCell className="text-right text-sm">{e.quantity} {e.unit}</TableCell>
                        <TableCell className="text-right text-sm font-medium">{(e.total_amount || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── SERBATOIO 3D TAB ───────────────── */}
        <TabsContent value="tank">
          <ProjectTankPanel projectId={projectId} />
        </TabsContent>
      </Tabs>

      {/* ─── ASSIGN EMPLOYEE DIALOG ─────────── */}
      <Dialog open={showAssignEmp} onOpenChange={setShowAssignEmp}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Assegna Personale al Progetto</DialogTitle>
            <DialogDescription>Seleziona i dipendenti da assegnare</DialogDescription>
          </DialogHeader>
          <Input placeholder="Cerca dipendente..." value={assignSearch} onChange={(e) => setAssignSearch(e.target.value)} data-testid="search-assign-emp" />
          <div className="max-h-60 overflow-y-auto border rounded-md divide-y">
            {allEmployees
              .filter(e => !assignedEmpIds.includes(e.id))
              .filter(e => !assignSearch || `${e.first_name} ${e.last_name}`.toLowerCase().includes(assignSearch.toLowerCase()))
              .map(e => (
                <div key={e.id} className="flex items-center justify-between p-2 hover:bg-slate-50">
                  <span className="text-sm">{e.first_name} {e.last_name} <span className="text-xs text-slate-400">({e.role || '-'})</span></span>
                  <Button size="sm" variant="outline" onClick={() => assignEmployee(e.id)} data-testid={`do-assign-emp-${e.id}`}>
                    <UserPlus size={14} />
                  </Button>
                </div>
              ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* ─── ASSIGN EQUIPMENT DIALOG ──────── */}
      <Dialog open={showAssignEquip} onOpenChange={setShowAssignEquip}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Assegna Attrezzatura al Progetto</DialogTitle>
            <DialogDescription>Seleziona le attrezzature da assegnare</DialogDescription>
          </DialogHeader>
          <Input placeholder="Cerca attrezzatura..." value={assignSearch} onChange={(e) => setAssignSearch(e.target.value)} data-testid="search-assign-equip" />
          <div className="max-h-60 overflow-y-auto border rounded-md divide-y">
            {allEquipment
              .filter(eq => !assignedEqIds.includes(eq.id))
              .filter(eq => !assignSearch || eq.name?.toLowerCase().includes(assignSearch.toLowerCase()))
              .map(eq => (
                <div key={eq.id} className="flex items-center justify-between p-2 hover:bg-slate-50">
                  <span className="text-sm">{eq.name} <span className="text-xs text-slate-400">({eq.type || '-'})</span></span>
                  <Button size="sm" variant="outline" onClick={() => assignEquipment(eq.id)} data-testid={`do-assign-eq-${eq.id}`}>
                    <Plus size={14} />
                  </Button>
                </div>
              ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* ─── PDO CREATE/EDIT DIALOG ──────── */}
      <Dialog open={showPdoCreate || !!showPdoEdit} onOpenChange={() => { setShowPdoCreate(false); setShowPdoEdit(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{showPdoEdit ? 'Modifica PDO' : 'Nuovo Permesso di Lavoro (PDO)'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Numero PDO</Label><Input value={pdoForm.numero} onChange={(e) => setPdoForm({ ...pdoForm, numero: e.target.value })} placeholder="Es: PDO-001" data-testid="pdo-numero" /></div>
            <div className="space-y-1.5">
              <Label>Tipo</Label>
              <Select value={pdoForm.tipo} onValueChange={(v) => setPdoForm({ ...pdoForm, tipo: v })}>
                <SelectTrigger data-testid="pdo-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="generico">Generico</SelectItem>
                  <SelectItem value="lavori_quota">Lavori in Quota</SelectItem>
                  <SelectItem value="spazi_confinati">Spazi Confinati</SelectItem>
                  <SelectItem value="lavori_caldo">Lavori a Caldo</SelectItem>
                  <SelectItem value="scavi">Scavi</SelectItem>
                  <SelectItem value="elettrico">Lavori Elettrici</SelectItem>
                  <SelectItem value="altro">Altro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Stato</Label>
              <Select value={pdoForm.status} onValueChange={(v) => setPdoForm({ ...pdoForm, status: v })}>
                <SelectTrigger data-testid="pdo-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="aperto">Aperto</SelectItem>
                  <SelectItem value="sospeso">Sospeso</SelectItem>
                  <SelectItem value="chiuso">Chiuso</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Data Apertura</Label><Input type="date" value={pdoForm.data_apertura} onChange={(e) => setPdoForm({ ...pdoForm, data_apertura: e.target.value })} data-testid="pdo-data-apertura" /></div>
            <div className="space-y-1.5"><Label>Data Chiusura</Label><Input type="date" value={pdoForm.data_chiusura} onChange={(e) => setPdoForm({ ...pdoForm, data_chiusura: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Area di Lavoro</Label><Input value={pdoForm.area_lavoro} onChange={(e) => setPdoForm({ ...pdoForm, area_lavoro: e.target.value })} placeholder="Es: Tank 215, Tetto" /></div>
            <div className="col-span-1 md:col-span-2 space-y-1.5"><Label>Attivita Previste</Label><Textarea value={pdoForm.attivita} onChange={(e) => setPdoForm({ ...pdoForm, attivita: e.target.value })} placeholder="Descrivere le attivita..." /></div>
            <div className="col-span-1 md:col-span-2 space-y-1.5"><Label>Descrizione</Label><Textarea value={pdoForm.descrizione} onChange={(e) => setPdoForm({ ...pdoForm, descrizione: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Rischi Identificati</Label><Textarea value={pdoForm.rischi} onChange={(e) => setPdoForm({ ...pdoForm, rischi: e.target.value })} placeholder="Rischi specifici..." /></div>
            <div className="space-y-1.5"><Label>Misure di Sicurezza</Label><Textarea value={pdoForm.misure_sicurezza} onChange={(e) => setPdoForm({ ...pdoForm, misure_sicurezza: e.target.value })} placeholder="DPI, procedure..." /></div>
            <div className="col-span-1 md:col-span-2 space-y-1.5">
              <Label>Lavoratori Coinvolti</Label>
              <div className="max-h-40 overflow-y-auto border rounded-md divide-y">
                {allEmployees.map(e => (
                  <div key={e.id} className="flex items-center gap-2 p-2 cursor-pointer hover:bg-slate-50"
                    onClick={() => togglePdoWorker(e.id)}>
                    <input type="checkbox" checked={pdoForm.worker_ids.includes(e.id)} readOnly className="rounded" />
                    <span className="text-sm">{e.first_name} {e.last_name}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="col-span-1 md:col-span-2 space-y-1.5"><Label>Note</Label><Textarea value={pdoForm.note} onChange={(e) => setPdoForm({ ...pdoForm, note: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowPdoCreate(false); setShowPdoEdit(null); }}>Annulla</Button>
            <Button onClick={showPdoEdit ? handlePdoEdit : handlePdoCreate} data-testid="save-pdo-btn">
              {showPdoEdit ? 'Salva Modifiche' : 'Crea PDO'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
